import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { resolveImg } from "@/lib/api";
import { AmazonLogo, GoodreadsLogo, Stars } from "@/components/site/brands";
import { ArrowUpRight } from "lucide-react";

export default function BookCard({ book, index = 0 }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.12 }}
      className="group bg-white border border-gray-100 rounded-3xl p-6 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] gold-glow"
      data-testid={`book-card-${book.slug}`}
    >
      <div className="flex flex-col items-center text-center gap-5 sm:grid sm:grid-cols-[185px_1fr] sm:gap-9 sm:text-left sm:items-start">
        <div>
          <img
            src={resolveImg(book.cover_image)}
            alt={`${book.title} book cover by Kate Mercer`}
            loading="lazy"
            decoding="async"
            className="book-shadow img-crisp rounded-lg w-[150px] sm:w-full h-auto"
          />
        </div>
        <div className="flex flex-col items-center sm:items-start">
          <span className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">{book.category}</span>
          <h3 className="font-display text-2xl sm:text-[30px] font-bold leading-tight mt-2">{book.title}</h3>
          {book.subtitle && <p className="text-sm text-gray-500 mt-1">{book.subtitle}</p>}
          <div className="flex items-center gap-2 mt-3">
            <Stars rating={book.goodreads_rating || 5} size={15} />
            {book.goodreads_rating > 0 && (
              <span className="text-xs text-gray-500">
                {book.goodreads_rating.toFixed(1)}
                {book.goodreads_ratings_count > 0 && ` · ${book.goodreads_ratings_count} ratings`}
              </span>
            )}
          </div>
        </div>
      </div>

      <p className="text-gray-600 leading-relaxed text-[15px] mt-6 sm:mt-7 text-center sm:text-left">{book.description}</p>

      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-7 sm:mt-8">
        <a
          href={book.amazon_link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`card-buy-${book.slug}`}
          className="btn-gold btn-pill btn-pill-sm"
        >
          Buy on <AmazonLogo className="h-3.5 w-auto" />
        </a>
        <Link
          to={`/books/${book.slug}`}
          data-testid={`card-learn-${book.slug}`}
          className="btn-pill btn-pill-sm border border-gray-300 hover:border-black transition-colors"
        >
          Learn More <ArrowUpRight size={15} />
        </Link>
        {book.goodreads_link && (
          <a
            href={book.goodreads_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`card-goodreads-${book.slug}`}
            className="btn-pill btn-pill-sm bg-[#F4F1EA] hover:bg-[#eae4d6] transition-colors"
          >
            <GoodreadsLogo className="h-4 w-auto" /> Reviews
          </a>
        )}
      </div>
    </motion.article>
  );
}
